"use client";

import { useTaskContext } from "@/contexts/TaskContext";
import UserMessage from "./messages/UserMessage";
import SystemMessage from "./messages/SystemMessage";
import BudgetPill from "./BudgetPill";

const phaseOrder: Record<string, number> = {
  idle: 0,
  submitted: 1,
  clarifying: 2,
  budget: 3,
  matching: 4,
  selecting: 5,
  confirmed: 6,
  working: 7,
  delivered: 8,
  approved: 9,
  rating: 10,
  completed: 11,
};

function reached(phase: string, target: string): boolean {
  return phaseOrder[phase] >= phaseOrder[target];
}

export default function ConversationHistory() {
  const { phase, task } = useTaskContext();
  if (!task) return null;

  const answeredEntries = task.questions
    .map((q, idx) => ({ q, idx, answer: task.answers[idx] }))
    .filter((e) => e.answer !== undefined);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
      {/* Original user request */}
      {reached(phase, "submitted") && <UserMessage>{task.description}</UserMessage>}

      {/* Before clarifying */}
      {reached(phase, "clarifying") && task.questions.length > 0 && (
        <SystemMessage>I need a few details to find the best agent for this task.</SystemMessage>
      )}

      {/* Each answered question */}
      {answeredEntries.map((e) => (
        <UserMessage key={`answer-${e.idx}`}>{e.answer}</UserMessage>
      ))}

      {/* After clarifying, before budget */}
      {reached(phase, "budget") && task.questions.length > 0 && (
        <SystemMessage>Let&apos;s set a budget for this task.</SystemMessage>
      )}

      {/* Budget pill once budget confirmed */}
      {reached(phase, "matching") && task.budget != null && <BudgetPill amount={task.budget} />}

      {/* Matching message */}
      {reached(phase, "matching") && <SystemMessage>Finding the best agents…</SystemMessage>}

      {/* Working message */}
      {reached(phase, "working") && task.selectedAgent && (
        <SystemMessage>{task.selectedAgent.name} is working on your task…</SystemMessage>
      )}

      {/* Paid confirmation */}
      {reached(phase, "approved") && task.selectedAgent && task.budget != null && (
        <SystemMessage>
          Paid ${task.budget.toFixed(2)} to {task.selectedAgent.name}.
        </SystemMessage>
      )}
    </div>
  );
}
